<script>
import AddSLA from './AddSLA.vue';
import EditSLA from './EditSLA.vue';
import TeletelSlaInboxAssignments from './TeletelSlaInboxAssignments.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from 'dashboard/routes/dashboard/settings/components/BaseSettingsHeader.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import WootLabel from 'dashboard/components-next/label/Label.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

import { mapGetters } from 'vuex';
import { convertSecondsToTimeUnit } from '@chatwoot/utils';
import { useAlert } from 'dashboard/composables';
import { picoSearch } from '@scmmishra/pico-search';

export default {
  components: {
    AddSLA,
    EditSLA,
    TeletelSlaInboxAssignments,
    SettingsLayout,
    BaseSettingsHeader,
    BaseTable,
    BaseTableRow,
    BaseTableCell,
    WootLabel,
    Icon,
    NextButton,
  },
  data() {
    return {
      loading: {},
      showAddPopup: false,
      showEditPopup: false,
      showDeleteConfirmationPopup: false,
      showArchiveConfirmationPopup: false,
      showArchived: false,
      selectedResponse: {},
      searchQuery: '',
    };
  },
  computed: {
    ...mapGetters({
      records: 'sla/getSLA',
      archivedRecords: 'sla/getArchivedSLA',
      uiFlags: 'sla/getUIFlags',
    }),
    deleteConfirmText() {
      return this.$t('SLA.DELETE.CONFIRM.YES');
    },
    deleteRejectText() {
      return this.$t('SLA.DELETE.CONFIRM.NO');
    },
    deleteMessage() {
      return ` ${this.selectedResponse.name}`;
    },
    tableHeaders() {
      return [
        this.$t('SLA.LIST.TABLE_HEADER.SLA'),
        this.$t('SLA.LIST.TABLE_HEADER.BUSINESS_HOURS'),
        this.$t('SLA.LIST.RESPONSE_TYPES.SHORT_HAND.FRT'),
        this.$t('SLA.LIST.RESPONSE_TYPES.SHORT_HAND.NRT'),
        this.$t('SLA.LIST.RESPONSE_TYPES.SHORT_HAND.RT'),
        this.$t('INTEGRATION_APPS.LIST.ACTIONS'),
      ];
    },
    filteredRecords() {
      const query = this.searchQuery.trim();
      if (!query) return this.records;
      return picoSearch(this.records, query, ['name', 'description']);
    },
  },
  mounted() {
    this.$store.dispatch('sla/get');
    this.$store.dispatch('inboxes/get');
    this.$store.dispatch('agents/get');
    this.$store.dispatch('teams/get');
    this.$store.dispatch('labels/get');
  },
  methods: {
    openAddPopup() {
      this.showAddPopup = true;
    },
    hideAddPopup() {
      this.showAddPopup = false;
    },
    openEditPopup(response) {
      this.selectedResponse = response;
      this.showEditPopup = true;
    },
    hideEditPopup() {
      this.showEditPopup = false;
    },
    openArchivePopup(response) {
      this.selectedResponse = response;
      this.showArchiveConfirmationPopup = true;
    },
    closeArchivePopup() {
      this.showArchiveConfirmationPopup = false;
    },
    async confirmArchive() {
      const { id } = this.selectedResponse;
      this.closeArchivePopup();
      this.loading[id] = true;
      try {
        await this.$store.dispatch('sla/archive', id);
        useAlert(this.$t('SLA.TELETEL.ARCHIVE.API.ARCHIVED'));
      } catch (error) {
        useAlert(error?.message || this.$t('SLA.TELETEL.ARCHIVE.API.ERROR'));
      } finally {
        this.loading[id] = false;
      }
    },
    async restoreSla(id) {
      this.loading[id] = true;
      try {
        await this.$store.dispatch('sla/restore', id);
        useAlert(this.$t('SLA.TELETEL.ARCHIVE.API.RESTORED'));
      } catch (error) {
        useAlert(error?.message || this.$t('SLA.TELETEL.ARCHIVE.API.ERROR'));
      } finally {
        this.loading[id] = false;
      }
    },
    openDeletePopup(response) {
      this.showDeleteConfirmationPopup = true;
      this.selectedResponse = response;
    },
    closeDeletePopup() {
      this.showDeleteConfirmationPopup = false;
    },
    confirmDeletion() {
      this.loading[this.selectedResponse.id] = true;
      this.closeDeletePopup();
      this.deleteSla(this.selectedResponse.id);
    },
    deleteSla(id) {
      this.$store
        .dispatch('sla/delete', id)
        .then(() => {
          useAlert(this.$t('SLA.DELETE.API.SUCCESS_MESSAGE'));
        })
        .catch(error => {
          // The server explains refusals (for example: the policy is already
          // applied to conversations). Swallowing that left the button looking
          // broken, so show the reason and keep the generic line as a fallback.
          useAlert(error?.message || this.$t('SLA.DELETE.API.ERROR_MESSAGE'));
        })
        .finally(() => {
          this.loading[this.selectedResponse.id] = false;
        });
    },
    displayTime(threshold) {
      const { time, unit } = convertSecondsToTimeUnit(threshold, {
        minute: 'm',
        hour: 'h',
        day: 'd',
      });
      if (!time) return '-';
      return `${time}${unit}`;
    },
  },
};
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('SLA.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('SLA.HEADER')"
        :description="$t('SLA.DESCRIPTION')"
        :link-text="$t('SLA.LEARN_MORE')"
        :search-placeholder="$t('SLA.SEARCH_PLACEHOLDER')"
        feature-name="sla"
      >
        <template v-if="records?.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('SLA.COUNT', { n: records.length }) }}
          </span>
        </template>
        <template #actions>
          <NextButton
            :label="$t('SLA.ADD_ACTION')"
            size="sm"
            @click="openAddPopup"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <div
        v-if="uiFlags.isDisabled"
        class="flex items-start gap-3 p-4 mb-4 rounded-lg bg-n-slate-2 border border-n-slate-4"
      >
        <Icon
          icon="i-lucide-shield-off"
          class="size-5 mt-0.5 text-n-slate-11 flex-shrink-0"
        />
        <div class="flex flex-col gap-1">
          <span class="text-heading-3 text-n-slate-12">
            {{ $t('SLA.TELETEL.DISABLED.TITLE') }}
          </span>
          <span class="text-body-main text-n-slate-11">
            {{ $t('SLA.TELETEL.DISABLED.MESSAGE') }}
          </span>
        </div>
      </div>
      <BaseTable
        :headers="tableHeaders"
        :items="filteredRecords"
        :no-data-message="
          !records.length
            ? $t('SLA.LIST.404')
            : searchQuery && !filteredRecords.length
              ? $t('SLA.SEARCH.NO_RESULTS')
              : ''
        "
      >
        <template #header-2>
          <div class="flex items-center gap-1">
            <span class="text-heading-3">
              {{ $t('SLA.LIST.RESPONSE_TYPES.SHORT_HAND.FRT') }}
            </span>
            <Icon
              v-tooltip.left="$t('SLA.LIST.RESPONSE_TYPES.FRT')"
              icon="i-lucide-info"
              class="size-3.5 text-n-slate-10 cursor-help"
            />
          </div>
        </template>
        <template #header-3>
          <div class="flex items-center gap-1">
            <span class="text-heading-3">
              {{ $t('SLA.LIST.RESPONSE_TYPES.SHORT_HAND.NRT') }}
            </span>
            <Icon
              v-tooltip.left="$t('SLA.LIST.RESPONSE_TYPES.NRT')"
              icon="i-lucide-info"
              class="size-3.5 text-n-slate-10 cursor-help"
            />
          </div>
        </template>
        <template #header-4>
          <div class="flex items-center gap-1">
            <span class="text-heading-3">
              {{ $t('SLA.LIST.RESPONSE_TYPES.SHORT_HAND.RT') }}
            </span>
            <Icon
              v-tooltip.left="$t('SLA.LIST.RESPONSE_TYPES.RT')"
              icon="i-lucide-info"
              class="size-3.5 text-n-slate-10 cursor-help"
            />
          </div>
        </template>
        <template #row="{ items }">
          <BaseTableRow v-for="sla in items" :key="sla.id" :item="sla">
            <template #default>
              <BaseTableCell>
                <div class="flex flex-col gap-1 min-w-0">
                  <span class="text-body-main text-n-slate-12 truncate">
                    {{ sla.name }}
                  </span>
                  <span class="text-body-main text-n-slate-11 line-clamp-1">
                    {{ sla.description }}
                  </span>
                </div>
              </BaseTableCell>

              <BaseTableCell class="w-40">
                <WootLabel
                  :label="
                    sla.only_during_business_hours
                      ? $t('SLA.LIST.BUSINESS_HOURS_ON')
                      : $t('SLA.LIST.BUSINESS_HOURS_OFF')
                  "
                  :color="sla.only_during_business_hours ? 'teal' : 'slate'"
                  compact
                >
                  <template #icon>
                    <Icon
                      :icon="
                        sla.only_during_business_hours
                          ? 'i-lucide-alarm-clock-check'
                          : 'i-lucide-alarm-clock-off'
                      "
                      class="size-3.5"
                      :class="
                        sla.only_during_business_hours
                          ? 'text-n-teal-11'
                          : 'text-n-slate-11'
                      "
                    />
                  </template>
                </WootLabel>
              </BaseTableCell>

              <BaseTableCell align="start" class="w-24">
                <span class="text-body-main text-n-slate-12">
                  {{ displayTime(sla.first_response_time_threshold) }}
                </span>
              </BaseTableCell>

              <BaseTableCell align="start" class="w-24">
                <span class="text-body-main text-n-slate-12">
                  {{ displayTime(sla.next_response_time_threshold) }}
                </span>
              </BaseTableCell>

              <BaseTableCell align="start" class="w-24">
                <span class="text-body-main text-n-slate-12">
                  {{ displayTime(sla.resolution_time_threshold) }}
                </span>
              </BaseTableCell>

              <BaseTableCell align="end" class="w-12">
                <div class="flex justify-end gap-1">
                  <NextButton
                    v-tooltip.top="$t('SLA.FORM.EDIT')"
                    icon="i-lucide-pen"
                    slate
                    sm
                    @click="openEditPopup(sla)"
                  />
                  <NextButton
                    v-tooltip.top="$t('SLA.TELETEL.ARCHIVE.ACTION')"
                    icon="i-lucide-archive"
                    slate
                    sm
                    :is-loading="loading[sla.id]"
                    @click="openArchivePopup(sla)"
                  />
                  <NextButton
                    v-tooltip.top="$t('SLA.FORM.DELETE')"
                    icon="i-woot-bin"
                    slate
                    sm
                    class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                    :is-loading="loading[sla.id]"
                    @click="openDeletePopup(sla)"
                  />
                </div>
              </BaseTableCell>
            </template>
          </BaseTableRow>
        </template>
      </BaseTable>

      <section v-if="archivedRecords.length" class="flex flex-col gap-3 mt-6">
        <button
          type="button"
          class="flex items-center gap-2 text-start text-heading-3 text-n-slate-12"
          @click="showArchived = !showArchived"
        >
          <Icon
            :icon="
              showArchived ? 'i-lucide-chevron-down' : 'i-lucide-chevron-right'
            "
            class="size-4 text-n-slate-10 rtl:-scale-x-100"
          />
          {{
            $t('SLA.TELETEL.ARCHIVE.SECTION_TITLE', {
              n: archivedRecords.length,
            })
          }}
        </button>
        <template v-if="showArchived">
          <p class="text-body-main text-n-slate-11">
            {{ $t('SLA.TELETEL.ARCHIVE.SECTION_DESCRIPTION') }}
          </p>
          <div
            v-for="sla in archivedRecords"
            :key="sla.id"
            class="flex items-center justify-between gap-4 px-3 py-2 rounded-lg bg-n-slate-2"
          >
            <div class="flex items-center gap-2 min-w-0">
              <span class="text-body-main text-n-slate-11 truncate">
                {{ sla.name }}
              </span>
              <WootLabel
                :label="$t('SLA.TELETEL.ARCHIVE.BADGE')"
                color="slate"
                compact
              />
            </div>
            <NextButton
              :label="$t('SLA.TELETEL.ARCHIVE.RESTORE')"
              icon="i-lucide-archive-restore"
              slate
              sm
              :is-loading="loading[sla.id]"
              @click="restoreSla(sla.id)"
            />
          </div>
        </template>
      </section>

      <template v-if="!uiFlags.isDisabled">
        <TeletelSlaInboxAssignments />
        <!-- Owner order 2026-09-29: settings hold the policies; compliance
             results live in Reports → SLA only. -->
        <router-link
          :to="{ name: 'sla_reports' }"
          class="self-start text-sm text-n-blue-11 hover:underline"
        >
          {{ $t('SLA.TELETEL.OPEN_REPORT') }}
        </router-link>
      </template>

      <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
        <AddSLA @close="hideAddPopup" />
      </woot-modal>

      <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
        <EditSLA :selected-response="selectedResponse" @close="hideEditPopup" />
      </woot-modal>

      <woot-delete-modal
        v-model:show="showArchiveConfirmationPopup"
        :on-close="closeArchivePopup"
        :on-confirm="confirmArchive"
        :title="$t('SLA.TELETEL.ARCHIVE.CONFIRM_TITLE')"
        :message="$t('SLA.TELETEL.ARCHIVE.CONFIRM_MESSAGE')"
        :message-value="deleteMessage"
        :confirm-text="$t('SLA.TELETEL.ARCHIVE.CONFIRM_YES')"
        :reject-text="$t('SLA.TELETEL.ARCHIVE.CONFIRM_NO')"
      />

      <woot-delete-modal
        v-model:show="showDeleteConfirmationPopup"
        :on-close="closeDeletePopup"
        :on-confirm="confirmDeletion"
        :title="$t('SLA.DELETE.CONFIRM.TITLE')"
        :message="$t('SLA.DELETE.CONFIRM.MESSAGE')"
        :message-value="deleteMessage"
        :confirm-text="deleteConfirmText"
        :reject-text="deleteRejectText"
      />
    </template>
  </SettingsLayout>
</template>
